import React, { useMemo } from 'react';
import {
  SubmissionScoreEdit,
  ISubmission,
  ISubmissionScoreEditAssignment, ClassNames,
} from '@/components/shared/Grading/Navigation/SubmissionScoreEdit';
import useGradingStore from '../useGradingStore';

interface SubmissionScoreProps {
  classNames?: ClassNames;
  editable?: boolean;
  labelText?: string;
  onChange?: () => any;
}

export const useSubmissionScore = () => {
  const assignment = useGradingStore(state => state.assignment);
  const submission = useGradingStore(state => state.submission);
  const questionAnswers = useGradingStore(state => state.questionAnswers);

  return useMemo(() => {
    if (!submission) return 0;
    if (assignment.assignment_details.type !== 'Assessment') return submission.total_score;

    // For assessments, scores aren't calculated until all answers are graded.
    // So for the score, we will display a running total of what the score will be after everything is graded
    // or the actual values persisted to the DB.
    return (
      submission.total_score_override
      || submission.total_score
      || questionAnswers.filter(qa => !!qa.score).reduce((acc, cur) => acc + parseFloat(cur.score), 0)
    );
  }, [questionAnswers, submission?.total_score, submission?.total_score_override]);
};

const SubmissionScore = ({ classNames = {}, editable = true, labelText, onChange }: SubmissionScoreProps) => {
  const assignment = useGradingStore(state => state.assignment);
  const submission = useGradingStore(state => state.submission);

  const score = useSubmissionScore();

  const submissionScoreAssignment = {
    ...assignment,
    maxScore: assignment.max_points.toString(),
  } as any as ISubmissionScoreEditAssignment;

  return (
    <SubmissionScoreEdit
      assignment={submissionScoreAssignment}
      classNames={{
        input: '[&>input]:tw-ml-0',
        label: 'tw-mb-2',
        root: 'tw-flex-col tw-items-start',
        saveStatus: 'tw-text-left tw-text-balance tw-whitespace-normal',
        ...classNames,
      }}
      editable={editable}
      labelText={labelText}
      submission={{ ...submission, total_score: score } as any as ISubmission}
      onChange={onChange}
    />
  );
};

export default SubmissionScore;
